import { ShellNavId } from 'shared/layout/Sidebar';
import { BadgeModes } from 'types/badge-modes';

/** Reserved top-level paths that are never profile ids. */
export const RESERVED_PATHS = new Set([
  'gallery',
  'journeys',
  'stats',
  'leaderboard',
  'explore',
  'create',
  'claim',
  'edit',
  'summary',
  'feed',
  'battle',
]);

export const BATTLE_HOW_TO_PATH = '/battle/how-to-play';
export const BATTLE_BADGES_PATH = '/battle/badges';
/** Legacy Claimed Only URL; redirects to `/battle/badges`. */
export const BATTLE_BADGES_CLAIMED_PATH = '/battle/badges/claimed';
export const BATTLE_ARENA_PATH = '/battle/arena';
export const BATTLE_LEADERBOARDS_PATH = '/battle/leaderboard';

/** First path segments under `/battle` that are modes, not room ids. */
export const BATTLE_PATH_SEGMENTS = ['how-to-play', 'badges', 'arena', 'leaderboard'] as const;

export const battleScenarioPath = (id: string): string => `${BATTLE_HOW_TO_PATH}/${id}`;

export const battleModeForPath = (seg1?: string): BadgeModes => {
  if (seg1 === 'how-to-play') return 'battle_how_to';
  if (seg1 === 'arena') return 'battle_arena';
  if (seg1 === 'leaderboard') return 'battle_leaderboards';
  return 'badges_battle';
};

export const isBattleMode = (mode: BadgeModes): boolean =>
  mode === 'badges_battle' || mode === 'battle_how_to' || mode === 'battle_arena' || mode === 'battle_leaderboards';

/** Claim-selection URLs (`/claim`) plus the legacy `/edit` bookmark. */
export const isClaimSelectSegment = (seg: string | undefined): boolean => seg === 'claim' || seg === 'edit';

/** Claim flow paths: claim/create badge selection and the summarize step. */
export const isClaimFlowPath = (pathname: string): boolean => {
  const seg = pathname.split('/').filter(Boolean)[0];
  return isClaimSelectSegment(seg) || seg === 'create' || seg === 'summary';
};

export const pathForNav = (nav: ShellNavId, profileId: string | null): string => {
  switch (nav) {
    case 'feed':
      return '/';
    case 'badge_gallery':
      return '/gallery/badges';
    case 'journey_gallery':
      return '/gallery/journeys';
    case 'stats':
      return '/stats';
    case 'badge_hashes':
      return '/stats/hashes';
    case 'leaderboard':
      return '/leaderboard';
    case 'explore':
      return '/explore';
    case 'badges_battle':
      return BATTLE_BADGES_PATH;
    case 'battle_how_to':
      return '/battle/how-to-play';
    case 'battle_arena':
      return '/battle/arena';
    case 'battle_leaderboards':
      return BATTLE_LEADERBOARDS_PATH;
    case 'claim':
      return claimPath(profileId);
    case 'collected':
      return profileId ? `/${profileId}` : '/create';
    case 'journeys':
      return profileId ? `/${profileId}/journeys` : '/create';
    case 'profile_feed':
      return profileId ? `/${profileId}/feed` : '/create';
    case 'submitted':
      return profileId ? `/${profileId}/contributions` : '/create';
    default:
      return '/';
  }
};

export const CONTRIBUTIONS_TABS = ['all', 'idea', 'design'] as const;
export type ContributionsTab = (typeof CONTRIBUTIONS_TABS)[number];

export const parseContributionsTab = (hash: string | undefined): ContributionsTab => {
  const tab = (hash ?? '').replace(/^#/, '');
  return tab === 'idea' || tab === 'design' ? tab : 'all';
};

/** Profile contributions section path (formerly `/submitted`). `#design` / `#idea` select those pills. */
export const contributionsPath = (profileId: string, tab: ContributionsTab = 'all'): string => {
  const base = `/${profileId}/contributions`;
  return tab === 'all' ? base : `${base}#${tab}`;
};

/** True if the path segment is the contributions section (incl. legacy `submitted`). */
export const isContributionsSegment = (seg: string | undefined): boolean =>
  seg === 'contributions' || seg === 'submitted';

/** Profile claim badges. Create stays for users without a sheet profile. */
export const CLAIM_PATH = '/claim';
/** Legacy claim URL; redirects to `/claim`. */
export const LEGACY_EDIT_PATH = '/edit';

export const claimPath = (profileId: string | null): string => (profileId ? CLAIM_PATH : '/create');

/** Summarize & Claim step (after selecting badges on /claim or /create). */
export const SUMMARY_PATH = '/summary';

/** Deep-link into the journeys gallery (optionally scrolled to a journey). */
export const journeysGalleryPath = (journeyId?: string): string =>
  journeyId ? `/gallery/journeys#${journeyId}` : '/gallery/journeys';
